import BackgroundImageHelper, {
  Container as BackgroundImageHelperContainer,
  Content as BackgroundImageHelperContent,
} from "@components/BackgroundImageHelper"
import colors from "@constants/colors"
import React from "react"
import styled, { css } from "styled-components"

export const Container = styled.div`
  display: flex;
  width: 100%;
  margin-top: auto;
`

const baseSwatchStyles = `
  margin-right: 16px;
  border: transparent;
  border-radius: 50%;
  cursor: pointer;

  &:active, &:focus {
    outline: none;
  }
`

const unavailableSwatchStyles = css`
  ${BackgroundImageHelperContainer} {
    picture {
    }
  }
  ${BackgroundImageHelperContent} {
    cursor: not-allowed;
    background: linear-gradient(
      to top left,
      rgba(0, 0, 0, 0) 0%,
      ${colors.offWhite40} calc(50% - 2px),
      ${colors.justAnotherGray},
      ${colors.offWhite40} calc(50% + 2px),
      rgba(0, 0, 0, 0) 100%
    );
  }
`

export const ColorWrapper = styled.div<{
  $active?: boolean
  $isUnavailable?: boolean
}>`
  ${({ $active, $isUnavailable }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    ${$active ? `background-color: ${colors.sideBarHeaderGray};` : ""}
    ${$isUnavailable ? unavailableSwatchStyles : ""}
  `}
`

const unavailableColorSwatchStyles = css`
  cursor: not-allowed;
`
export const ColorSwatch = styled.button<any>`
  ${({ $hexString, $isUnavailable, $size }) => css`
    ${baseSwatchStyles}
    position: relative;
    width: ${$size};
    height: ${$size};
    padding: 0;
    background-color: #${$hexString || "000000"};
    border: ${$hexString && $hexString.toUpperCase() === "FFFFFF"
      ? `1px solid ${colors.borderGray}`
      : "none"};
    ${$isUnavailable ? unavailableColorSwatchStyles : ""}
  `}
`

const Unavailable = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(
    to top left,
    rgba(0, 0, 0, 0) 0%,
    ${colors.offWhite40} calc(50% - 2px),
    ${colors.justAnotherGray},
    ${colors.offWhite40} calc(50% + 2px),
    rgba(0, 0, 0, 0) 100%
  );
`

export const ImageSwatch = styled.button<{ $size: string }>`
  ${({ $size }) => css`
    ${baseSwatchStyles}
    margin: 0;
    padding: 0;
    background-color: transparent;

    ${BackgroundImageHelperContainer} {
      ${baseSwatchStyles}
      width: ${$size};
      height: ${$size};
      padding: 0;
      overflow: hidden;
      background-color: transparent;
    }
  `}
`

export const ActiveIndicator = styled.div<{ $width: string }>`
  margin-top: 14px;
  margin-right: 20px;
  height: 1.29px;
  width: ${(props) => props.$width};
  background: #181818;
`

export interface SwatchProps {
  active?: boolean
  hexString?: string
  isUnavailable?: boolean
  name: string
  onClick: any
  swatchImage?: string
}

interface ColorSwatchesProps {
  activeSwatchName?: string
  colors: Array<SwatchProps>
  size?: string
}

const ColorSwatches: React.FC<ColorSwatchesProps> = ({
  activeSwatchName,
  colors,
  size = "20px",
}: ColorSwatchesProps) => (
  <Container>
    {colors.map((color: SwatchProps, idx: number) => {
      const title = color.isUnavailable
        ? `${color.name} (Out of Stock)`
        : color.name

      return (
        <ColorWrapper
          $active={color.active || false}
          $isUnavailable={color.isUnavailable}
          key={idx}
          title={title}
        >
          {color.swatchImage ? (
            <ImageSwatch $size={size} onClick={color.onClick} title={title}>
              <BackgroundImageHelper src={color.swatchImage} />
            </ImageSwatch>
          ) : (
            <ColorSwatch
              $hexString={color.hexString}
              $isUnavailable={color.isUnavailable}
              $size={size}
              onClick={color.onClick}
              title={title}
            >
              {color.isUnavailable && <Unavailable />}
            </ColorSwatch>
          )}
          {activeSwatchName === color.name && <ActiveIndicator $width={size} />}
        </ColorWrapper>
      )
    })}
  </Container>
)

export default ColorSwatches
